/**
 * Error Handler Service
 * ============================================================================
 *
 * Purpose:
 * --------
 * Centralized error handling service using functional programming.
 * Provides pure functions for error processing, transformation, and recovery.
 *
 * This service handles:
 * - Error normalization and transformation
 * - Error severity classification
 * - Recovery strategy determination
 * - Error reporting (to monitoring services)
 * - Local error logging
 *
 * Design Principles:
 * ------------------
 * - Functional programming (no classes!)
 * - Pure functions where possible
 * - Composable error handlers
 * - Framework-agnostic core logic
 * - Side effects isolated and explicit
 *
 * Non-goals:
 * ----------
 * - UI rendering (handled by components)
 * - React-specific logic (handled by hooks)
 * - Direct DOM manipulation
 *
 * Usage:
 * ------
 * ```typescript
 * import { handleError, createAppError } from 'core/error-handling';
 *
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   handleError(error, { source: 'MyComponent' });
 * }
 * ```
 * ============================================================================
 */

import type {
  AppError,
  ErrorCategory,
  ErrorContext,
  ErrorHandlerState,
  ErrorHandlingOptions,
  ErrorReporter,
  ErrorSeverity,
  RecoveryConfig,
  StoredError,
} from './types';
import {
  ERROR_CATEGORY,
  ERROR_CONFIG,
  ERROR_I18N_KEYS,
  ERROR_SEVERITY,
  ERROR_STORAGE_KEYS,
  RECOVERY_STRATEGY,
} from './constants';

import i18n from 'locales';

const state: ErrorHandlerState = {
  lastErrorTime: 0,
  errorReporter: null,
  errorLog: [],
};

/**
 * Type guard to check if error is AppError
 */
export const isAppError = (error: unknown): error is AppError =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  'severity' in error &&
  'category' in error;

/**
 * Creates a normalized AppError from any error type
 *
 * Transforms native Error, API errors, or unknown errors into
 * standardized AppError format.
 *
 * @param error - Error to normalize
 * @param context - Additional context
 * @returns Normalized AppError
 */
export const createAppError = (error: unknown, context?: ErrorContext): AppError => {
  // Already an AppError
  if (isAppError(error)) {
    return {
      ...error,
      context: { ...error.context, ...context },
    };
  }

  // Native Error
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      code: 'UNKNOWN_ERROR',
      severity: ERROR_SEVERITY.MEDIUM,
      category: ERROR_CATEGORY.RUNTIME,
      stack: error.stack,
      timestamp: new Date(),
      context,
      originalError: error,
    };
  }

  // Unknown error type
  return {
    name: 'UnknownError',
    message: String(error),
    code: 'UNKNOWN_ERROR',
    severity: ERROR_SEVERITY.MEDIUM,
    category: ERROR_CATEGORY.UNKNOWN,
    timestamp: new Date(),
    context,
  };
};

/**
 * Determines error severity based on code and category
 *
 * Pure function that classifies errors by severity.
 */
export const determineSeverity = (code: string, category: ErrorCategory): ErrorSeverity => {
  // Critical errors
  if (category === ERROR_CATEGORY.AUTH || code.includes('CRITICAL') || code.includes('FATAL')) {
    return ERROR_SEVERITY.CRITICAL;
  }

  // High priority errors
  if (
    category === ERROR_CATEGORY.NETWORK ||
    category === ERROR_CATEGORY.BUSINESS ||
    code.includes('FAILED')
  ) {
    return ERROR_SEVERITY.HIGH;
  }

  // Low priority errors
  if (category === ERROR_CATEGORY.EXTERNAL || code.includes('WARNING')) {
    return ERROR_SEVERITY.LOW;
  }

  // Default to medium
  return ERROR_SEVERITY.MEDIUM;
};

/**
 * Determines appropriate recovery strategy for an error
 *
 * Pure function that suggests recovery strategy based on error type.
 */
export const determineRecoveryStrategy = (error: AppError): RecoveryConfig => {
  const { category, code, severity } = error;

  // Auth errors -> redirect to login
  if (category === ERROR_CATEGORY.AUTH || code === 'UNAUTHORIZED') {
    return {
      strategy: RECOVERY_STRATEGY.REDIRECT,
      redirectTo: '/login',
    };
  }

  // Network errors -> retry
  if (category === ERROR_CATEGORY.NETWORK && severity !== ERROR_SEVERITY.CRITICAL) {
    return {
      strategy: RECOVERY_STRATEGY.RETRY,
      maxRetries: ERROR_CONFIG.MAX_RETRY_ATTEMPTS,
      retryDelay: ERROR_CONFIG.RETRY_DELAY_MS,
    };
  }

  // Component errors -> reload
  if (category === ERROR_CATEGORY.COMPONENT) {
    return {
      strategy: RECOVERY_STRATEGY.RELOAD,
    };
  }

  // Validation errors -> no recovery (user must fix input)
  if (category === ERROR_CATEGORY.VALIDATION) {
    return {
      strategy: RECOVERY_STRATEGY.NONE,
    };
  }

  // Default -> no automatic recovery
  return {
    strategy: RECOVERY_STRATEGY.NONE,
  };
};

/**
 * Gets user-friendly error message using i18n
 *
 * Translates error code to localized message.
 * Falls back to error.message if translation not found.
 */
export const getUserFriendlyMessage = (error: AppError): string => {
  // Use translation key if provided
  if (error.translationKey) {
    const translated = error.translationParams
      ? i18n.t(error.translationKey, { defaultValue: error.message, ...error.translationParams })
      : i18n.t(error.translationKey, { defaultValue: error.message });
    if (translated !== error.translationKey) {
      return translated;
    }
  }

  // Try to find translation by category and code
  const categoryKey = ERROR_I18N_KEYS[error.category.toUpperCase() as keyof typeof ERROR_I18N_KEYS];
  if (categoryKey) {
    const key = `${categoryKey}.${error.code}`;
    const translated = i18n.t(key, { defaultValue: error.message });
    if (translated !== error.message) {
      return translated;
    }
  }

  // Fall back to generic error message
  const genericKey = `${ERROR_I18N_KEYS.GENERIC}.unknown`;
  const genericMessage = i18n.t(genericKey, { defaultValue: error.message });

  // If translation exists, use it, otherwise use error message
  return genericMessage !== genericKey ? genericMessage : error.message;
};

/**
 * Checks if error should be throttled
 *
 * Prevents error spam by throttling notifications.
 */
export const shouldThrottleError = (): boolean => {
  const now = Date.now();
  const timeSinceLastError = now - state.lastErrorTime;

  if (timeSinceLastError < ERROR_CONFIG.ERROR_THROTTLE_MS) {
    return true;
  }

  state.lastErrorTime = now;
  return false;
};

/**
 * Stores error in local log
 *
 * Side effect: Updates local storage.
 */
export const storeErrorLocally = (error: AppError): void => {
  try {
    const storedError: StoredError = {
      code: error.code,
      message: error.message,
      severity: error.severity,
      category: error.category,
      context: error.context,
      timestamp: error.timestamp.toISOString(),
      reported: error.reported ?? false,
    };

    // Add to in-memory log
    state.errorLog.unshift(storedError);

    // Limit log size
    if (state.errorLog.length > ERROR_CONFIG.MAX_LOCAL_ERRORS) {
      state.errorLog = state.errorLog.slice(0, ERROR_CONFIG.MAX_LOCAL_ERRORS);
    }

    // Persist to localStorage
    localStorage.setItem(ERROR_STORAGE_KEYS.ERROR_LOG, JSON.stringify(state.errorLog));
  } catch {
    // Silently fail - error logging is not critical
  }
};

/**
 * Retrieves error log from storage
 */
export const getErrorLog = (): StoredError[] => {
  try {
    const stored = localStorage.getItem(ERROR_STORAGE_KEYS.ERROR_LOG);
    if (stored) {
      const parsed = JSON.parse(stored) as StoredError[];
      state.errorLog = parsed;
      return state.errorLog;
    }
  } catch {
    // Ignore parse errors
  }
  return [];
};

/**
 * Clears error log
 *
 * Side effect: Clears local storage.
 */
export const clearErrorLog = (): void => {
  state.errorLog = [];
  try {
    localStorage.removeItem(ERROR_STORAGE_KEYS.ERROR_LOG);
  } catch {
    // Silently fail
  }
};

/**
 * Sets error reporter instance
 *
 * Used to integrate with external monitoring (Sentry, etc.)
 */
export const setErrorReporter = (reporter: ErrorReporter | null): void => {
  state.errorReporter = reporter;
};

/**
 * Reports error to monitoring service
 *
 * Side effect: Sends error to external service.
 */
export const reportError = async (error: AppError): Promise<AppError> => {
  if (!state.errorReporter) {
    return error;
  }

  try {
    await state.errorReporter.report(error);
    return { ...error, reported: true };
  } catch (reportError) {
    // Log reporting failure but don't throw
    if (ERROR_CONFIG.SHOW_ERROR_DETAILS) {
      console.error('Failed to report error:', reportError);
    }
    return error;
  }
};

/**
 * Logs error to console (in development)
 */
export const logToConsole = (error: AppError): void => {
  if (!ERROR_CONFIG.SHOW_ERROR_DETAILS) {
    return;
  }

  const style = {
    critical: 'color: #ff0000; font-weight: bold;',
    high: 'color: #ff6600; font-weight: bold;',
    medium: 'color: #ffaa00;',
    low: 'color: #888888;',
    info: 'color: #0088ff;',
  };

  console.group(`%c[${error.severity.toUpperCase()}] ${error.category}`, style[error.severity]);
  console.error('Message:', error.message);
  console.error('Code:', error.code);
  if (error.context) {
    console.error('Context:', error.context);
  }
  if (error.stack) {
    console.error('Stack:', error.stack);
  }
  if (error.originalError) {
    console.error('Original:', error.originalError);
  }
  console.groupEnd();
};

/**
 * Determines handling options based on error properties
 *
 * Pure function that returns appropriate handling configuration.
 */
export const determineHandlingOptions = (
  error: AppError,
  options?: Partial<ErrorHandlingOptions>
): ErrorHandlingOptions => {
  const defaultOptions: ErrorHandlingOptions = {
    report: error.severity !== ERROR_SEVERITY.LOW && error.severity !== ERROR_SEVERITY.INFO,
    notify: error.severity !== ERROR_SEVERITY.LOW && error.severity !== ERROR_SEVERITY.INFO,
    logToConsole: ERROR_CONFIG.SHOW_ERROR_DETAILS,
    storeLocally: true,
    recovery: determineRecoveryStrategy(error),
  };

  return { ...defaultOptions, ...options };
};

/**
 * Main error handler function
 *
 * Centralized entry point for all error handling.
 * Side effects: May show notifications, report errors, store logs.
 *
 * @param error - Error to handle
 * @param context - Additional context
 * @param options - Handling options
 */
export const handleError = async (
  error: unknown,
  context?: ErrorContext,
  options?: Partial<ErrorHandlingOptions>
): Promise<void> => {
  // Normalize error
  let appError = createAppError(error, context);

  // Determine handling options
  const handlingOptions = determineHandlingOptions(appError, options);

  // Log to console (dev mode)
  if (handlingOptions.logToConsole) {
    logToConsole(appError);
  }

  // Store locally
  if (handlingOptions.storeLocally) {
    storeErrorLocally(appError);
  }

  // Report to monitoring
  if (handlingOptions.report && !appError.reported) {
    appError = await reportError(appError);
  }

  // Note: Notifications are handled by the hook/component layer
  // to avoid direct UI coupling in the service layer
};

/**
 * Creates a custom error handler with preset context
 *
 * Functional composition for creating specialized error handlers.
 *
 * @example
 * const apiErrorHandler = createErrorHandler({ source: 'API' });
 * apiErrorHandler(error);
 */
export const createErrorHandler =
  (defaultContext?: ErrorContext, defaultOptions?: Partial<ErrorHandlingOptions>) =>
  (error: unknown, context?: ErrorContext, options?: Partial<ErrorHandlingOptions>) =>
    handleError(error, { ...defaultContext, ...context }, { ...defaultOptions, ...options });

/**
 * Wraps an async function with error handling
 *
 * Higher-order function for automatic error handling.
 *
 * @example
 * const safeApiCall = withErrorHandling(
 *   apiCall,
 *   { source: 'API', action: 'fetchData' }
 * );
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withErrorHandling = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context?: ErrorContext,
  options?: Partial<ErrorHandlingOptions>
): T =>
  (async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return await fn(...args);
    } catch (error) {
      await handleError(error, context, options);
      throw error; // Re-throw after handling
    }
  }) as T;
